import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import HandoffReport from "@/components/HandoffReport";
import { FileDown, ShieldCheck, Printer, ArrowLeft, Award } from "lucide-react";

const STATUS_LABEL = {
  ordered: "Ordered",
  in_process: "In Process",
  awaiting_arrival: "Awaiting Materials",
  scheduling: "Scheduling",
  scheduled: "Scheduled",
  installed: "Installed · Ready to sign",
  completed: "Completed · Signed",
};

export default function ClientHandoff() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/client/projects");
        setProjects(r.data || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load projects");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const eligible = projects.filter((p) => p.status === "installed" || p.status === "completed");

  return (
    <div data-testid="client-handoff-list" className="space-y-6">
      <div>
        <p className="overline">Documentation</p>
        <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-1">
          Handoff Reports
        </h1>
        <p className="text-sm text-[#475569] dark:text-slate-400 mt-1">
          Your official PowerBuilt certificate for each installed system — sign to
          activate your warranty and unlock the expansion flow.
        </p>
      </div>

      {loading ? (
        <div className="text-sm text-[#94A3B8]">Loading…</div>
      ) : eligible.length === 0 ? (
        <div className="card-pb p-12 text-center">
          <ShieldCheck className="w-8 h-8 mx-auto text-[#C9A356] mb-2 opacity-50" />
          <p className="text-[#475569] dark:text-slate-400">
            No handoff reports yet. Reports become available once your project reaches the
            <b> Installed</b> stage.
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {eligible.map((p) => (
            <Link
              key={p.project_id}
              to={`/dashboard/handoff/${p.project_id}`}
              className="card-pb p-5 block hover:-translate-y-0.5 transition-all"
              data-testid={`handoff-card-${p.project_id}`}
            >
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="text-[10px] font-mono text-[#94A3B8]">
                    {p.project_number || p.project_id}
                  </div>
                  <h3 className="font-heading text-lg text-[#0A2540] dark:text-white mt-1 truncate">
                    {p.title}
                  </h3>
                  <div className="text-xs text-[#475569] dark:text-slate-400 mt-1">
                    {(p.project_type || "").replace(/_/g, " ")}
                  </div>
                </div>
                <span
                  className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full whitespace-nowrap ${
                    p.status === "completed"
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-amber-100 text-amber-700"
                  }`}
                >
                  {STATUS_LABEL[p.status] || p.status}
                </span>
              </div>
              <div className="mt-4 inline-flex items-center gap-1 text-xs text-[#C9A356]">
                {p.status === "completed" ? (
                  <>
                    <Award className="w-3.5 h-3.5" /> View signed certificate
                  </>
                ) : (
                  <>
                    <FileDown className="w-3.5 h-3.5" /> Open & sign report
                  </>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function ClientHandoffDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [b, setB] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/client/projects/${id}/handoff`);
      setB(r.data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 404) {
        setErr("This handoff report is not available. The project may not exist or belong to another account.");
      } else if (typeof detail === "object" && detail?.error === "handoff_not_ready") {
        setErr("This project's handoff report is not ready yet. It becomes available once the install is complete.");
      } else {
        setErr(typeof detail === "string" ? detail : "Failed to load report");
      }
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (err) {
    return (
      <div className="card-pb p-12 text-center max-w-lg mx-auto">
        <p className="text-sm text-[#475569] dark:text-slate-400">{err}</p>
        <button
          onClick={() => navigate("/dashboard/handoff")}
          className="btn-accent text-sm mt-4"
          data-testid="handoff-back-btn"
        >
          Back to projects
        </button>
      </div>
    );
  }
  if (!b) return <div className="text-sm text-[#94A3B8]">Preparing report…</div>;

  return (
    <div data-testid="client-handoff-detail">
      <div className="print:hidden flex items-center justify-between max-w-4xl mx-auto mb-4">
        <Link
          to="/dashboard/handoff"
          data-testid="handoff-back-btn"
          className="text-xs text-[#C9A356] inline-flex items-center gap-1"
        >
          <ArrowLeft className="w-3 h-3" /> All reports
        </Link>
        <button
          onClick={() => window.print()}
          className="btn-accent text-sm inline-flex items-center gap-1"
          data-testid="print-client-handoff"
        >
          <Printer className="w-4 h-4" /> Print / Save PDF
        </button>
      </div>
      <HandoffReport bundle={b} mode="client" onSigned={load} />
    </div>
  );
}
